import { foldText, nameKey } from './names';
import type { PlaceKind } from './scoring';

/**
 * Bulk import of places (docs/place-import.md): the vocabulary shared by the API and the panel.
 *
 *  - which fields a file may carry and under which column names they usually come
 *  - how a raw cell becomes a value (text, coordinates, category, kind...) without touching what the
 *    person wrote beyond whitespace and control characters
 *  - what is wrong with a row, in words an administrator can act on
 *
 * Everything here is pure: no database, no network. What needs them (municipalities, duplicates,
 * geocoding) lives in apps/api/src/modules/location/import.
 */

// ---------------------------------------------------------------------------- catalogs
export const PLACE_CATEGORIES = [
  'hotel', 'clinic', 'hospital', 'school', 'university', 'restaurant', 'business', 'shopping', 'residential',
  'airport', 'terminal', 'church', 'park', 'government', 'landmark', 'other',
] as const;
export type PlaceCategory = (typeof PLACE_CATEGORIES)[number];

/** Kinds a tenant may create (the CHECK of migration 0016): operational or detail places. */
export const TENANT_PLACE_KINDS = ['pickup_point', 'poi', 'landmark', 'conjunto', 'urbanizacion', 'sector'] as const satisfies readonly PlaceKind[];
export type TenantPlaceKind = (typeof TENANT_PLACE_KINDS)[number];

/** Folded for matching column names and catalog values: "Código DANE" -> "codigodane". */
export const foldKey = (s: string): string => foldText(s).replace(/\s+/g, '');

const CATEGORY_SYNONYMS: Record<PlaceCategory, string[]> = {
  hotel: ['hotel', 'hoteles', 'hostal', 'hospedaje', 'alojamiento', 'motel', 'posada', 'lodging', 'fincahotel'],
  clinic: ['clinica', 'clinicas', 'consultorio', 'centromedico', 'centrodesalud', 'ips', 'laboratorio', 'odontologia', 'clinic', 'salud'],
  hospital: ['hospital', 'hospitales', 'urgencias'],
  school: ['colegio', 'colegios', 'escuela', 'institucioneducativa', 'jardininfantil', 'jardin', 'guarderia', 'school', 'educacion'],
  university: ['universidad', 'universidades', 'university', 'instituto', 'institutotecnologico', 'educacionsuperior'],
  restaurant: ['restaurante', 'restaurantes', 'restaurant', 'cafe', 'cafeteria', 'bar', 'panaderia', 'comida', 'comidas', 'food', 'gastronomia'],
  business: ['empresa', 'empresas', 'negocio', 'comercio', 'oficina', 'tienda', 'almacen', 'banco', 'fabrica', 'industria', 'bodega', 'drogueria', 'farmacia', 'business', 'office', 'shop', 'store'],
  shopping: ['centrocomercial', 'centroscomerciales', 'mall', 'shopping', 'supermercado', 'plazademercado', 'hipermercado'],
  residential: ['residencial', 'conjunto', 'conjuntoresidencial', 'unidadresidencial', 'unidad', 'edificio', 'vivienda', 'apartamentos', 'urbanizacion', 'parcelacion', 'residential'],
  airport: ['aeropuerto', 'airport'],
  terminal: ['terminal', 'terminaldetransporte', 'terminaldetransportes', 'estacion', 'estaciondebuses', 'paradero', 'station'],
  church: ['iglesia', 'parroquia', 'templo', 'capilla', 'catedral', 'church'],
  park: ['parque', 'parques', 'plaza', 'cancha', 'coliseo', 'estadio', 'unidaddeportiva', 'park', 'recreacion'],
  government: ['entidadpublica', 'alcaldia', 'gobernacion', 'notaria', 'juzgado', 'policia', 'estaciondepolicia', 'bomberos', 'government', 'gobierno'],
  landmark: ['referencia', 'puntodereferencia', 'monumento', 'hito', 'landmark'],
  other: ['otro', 'otros', 'otra', 'other', 'varios'],
};
const CATEGORY_INDEX = new Map<string, PlaceCategory>();
for (const [cat, words] of Object.entries(CATEGORY_SYNONYMS) as Array<[PlaceCategory, string[]]>) {
  CATEGORY_INDEX.set(cat, cat);
  for (const w of words) if (!CATEGORY_INDEX.has(w)) CATEGORY_INDEX.set(w, cat);
}

/** Category of the controlled catalog for a free text ("Clínica", "centro comercial"), or null. Exact words only: no guessing. */
export function matchCategory(raw: string): PlaceCategory | null {
  return CATEGORY_INDEX.get(foldKey(raw)) ?? null;
}

const KIND_SYNONYMS: Record<TenantPlaceKind, string[]> = {
  pickup_point: ['pickuppoint', 'pickup', 'puntoderecogida', 'recogida', 'puntodeencuentro', 'paradero'],
  poi: ['poi', 'sitio', 'lugar', 'establecimiento', 'puntodeinteres', 'comercio', 'negocio'],
  landmark: ['landmark', 'referencia', 'puntodereferencia', 'hito'],
  conjunto: ['conjunto', 'conjuntoresidencial', 'unidadresidencial', 'unidad', 'edificio'],
  urbanizacion: ['urbanizacion', 'parcelacion'],
  sector: ['sector', 'zona'],
};
const KIND_INDEX = new Map<string, TenantPlaceKind>();
for (const [kind, words] of Object.entries(KIND_SYNONYMS) as Array<[TenantPlaceKind, string[]]>) for (const w of words) KIND_INDEX.set(w, kind);
/** Administrative kinds: official data of the platform, never imported by a company. */
const OFFICIAL_KIND_WORDS = new Set(['barrio', 'vereda', 'corregimiento', 'centropoblado', 'municipio', 'departamento', 'comuna']);

export function matchKind(raw: string): { kind: TenantPlaceKind } | { official: true } | null {
  const k = foldKey(raw);
  const kind = KIND_INDEX.get(k);
  if (kind) return { kind };
  return OFFICIAL_KIND_WORDS.has(k) ? { official: true } : null;
}

// ---------------------------------------------------------------------------- fields
export type ImportFieldKey =
  | 'name' | 'altName' | 'description' | 'externalId' | 'kind' | 'category' | 'subcategory' | 'tags'
  | 'country' | 'department' | 'municipality' | 'municipalityCode' | 'locality' | 'address' | 'postalCode'
  | 'lat' | 'lng' | 'coordinates' | 'crs' | 'radiusM' | 'instructions'
  | 'phone' | 'website' | 'hours' | 'accessibility' | 'status';

export interface ImportFieldDef {
  key: ImportFieldKey;
  label: string;
  group: 'Identificación' | 'Clasificación' | 'Ubicación administrativa' | 'Ubicación geográfica' | 'Información adicional';
  /** what the administrator reads in the data dictionary of the template */
  description: string;
  example: string;
  /** column of the official template */
  column: string;
  /** folded column names recognised automatically, most specific first */
  aliases: string[];
  maxLength: number;
}

export const IMPORT_FIELDS: readonly ImportFieldDef[] = [
  { key: 'name', label: 'Nombre', group: 'Identificación', column: 'nombre', maxLength: 160, example: 'Clínica Somer',
    description: 'Nombre del lugar tal como lo conoce la gente. Obligatorio.',
    aliases: ['nombre', 'name', 'nombredellugar', 'nombrelugar', 'lugar', 'sitio', 'establecimiento', 'razonsocial', 'titulo', 'title'] },
  { key: 'altName', label: 'Nombre alternativo', group: 'Identificación', column: 'nombre_alternativo', maxLength: 400, example: 'Somer; La Somer',
    description: 'Otros nombres con que se pide el lugar. Varios separados por punto y coma. Se guardan como alias.',
    aliases: ['nombrealternativo', 'nombresalternativos', 'alias', 'otronombre', 'otrosnombres', 'nombrecomun', 'sigla', 'altname', 'alternatename'] },
  { key: 'description', label: 'Descripción', group: 'Identificación', column: 'descripcion', maxLength: 500, example: 'Entrada principal por la calle 38',
    description: 'Texto libre de hasta 500 caracteres.',
    aliases: ['descripcion', 'description', 'detalle', 'observaciones', 'notas'] },
  { key: 'externalId', label: 'Identificador externo', group: 'Identificación', column: 'id_externo', maxLength: 80, example: 'SED-0042',
    description: 'Código del lugar en el sistema de origen. Si se repite en otra importación, actualiza el mismo lugar en vez de crear otro.',
    aliases: ['idexterno', 'identificadorexterno', 'externalid', 'id', 'identificador', 'codigo', 'code', 'codigointerno', 'ref'] },
  { key: 'kind', label: 'Tipo de lugar', group: 'Clasificación', column: 'tipo', maxLength: 60, example: 'Sitio',
    description: 'Punto de recogida, Sitio, Referencia, Conjunto, Urbanización o Sector. Vacío: el tipo elegido al importar.',
    aliases: ['tipodelugar', 'tipolugar', 'tipo', 'kind', 'type', 'clase'] },
  { key: 'category', label: 'Categoría', group: 'Clasificación', column: 'categoria', maxLength: 80, example: 'Clínica',
    description: 'Hotel, Clínica, Hospital, Colegio, Universidad, Restaurante, Empresa, Centro comercial, Residencial, Aeropuerto, Terminal, Iglesia, Parque, Entidad pública, Referencia u Otro.',
    aliases: ['categoria', 'category', 'rubro'] },
  { key: 'subcategory', label: 'Subcategoría', group: 'Clasificación', column: 'subcategoria', maxLength: 80, example: 'Urgencias 24 horas',
    description: 'Detalle libre dentro de la categoría.',
    aliases: ['subcategoria', 'subcategory'] },
  { key: 'tags', label: 'Etiquetas', group: 'Clasificación', column: 'etiquetas', maxLength: 300, example: 'salud; 24 horas',
    description: 'Palabras clave separadas por punto y coma (máximo 10).',
    aliases: ['etiquetas', 'tags', 'palabrasclave', 'keywords'] },
  { key: 'country', label: 'País', group: 'Ubicación administrativa', column: 'pais', maxLength: 60, example: 'Colombia',
    description: 'Solo se aceptan lugares de Colombia. Puede omitirse.',
    aliases: ['pais', 'country', 'countrycode', 'codigopais'] },
  { key: 'department', label: 'Departamento', group: 'Ubicación administrativa', column: 'departamento', maxLength: 80, example: 'Antioquia',
    description: 'Distingue municipios con el mismo nombre (Rionegro, Antioquia y Rionegro, Santander).',
    aliases: ['departamento', 'depto', 'dpto', 'department', 'state', 'provincia'] },
  { key: 'municipality', label: 'Municipio', group: 'Ubicación administrativa', column: 'municipio', maxLength: 80, example: 'Rionegro',
    description: 'Nombre oficial del municipio. Obligatorio si no hay código DANE, coordenadas ni municipio por defecto.',
    aliases: ['municipio', 'mpio', 'ciudad', 'municipality', 'city', 'town', 'localidad'] },
  { key: 'municipalityCode', label: 'Código DANE del municipio', group: 'Ubicación administrativa', column: 'codigo_dane', maxLength: 12, example: '05615',
    description: 'Código DIVIPOLA de cinco dígitos. Es la forma más segura de indicar el municipio.',
    aliases: ['codigodane', 'codigodanemunicipio', 'codigodivipola', 'divipola', 'codigomunicipio', 'codmunicipio', 'codmpio', 'mpiocdpmp', 'dane'] },
  { key: 'locality', label: 'Barrio, vereda o corregimiento', group: 'Ubicación administrativa', column: 'barrio_vereda', maxLength: 120, example: 'El Porvenir',
    description: 'Informativo: se guarda con el lugar.',
    aliases: ['barriovereda', 'barrio', 'vereda', 'corregimiento', 'comuna', 'neighborhood', 'neighbourhood', 'localidad', 'sector'] },
  { key: 'address', label: 'Dirección', group: 'Ubicación administrativa', column: 'direccion', maxLength: 200, example: 'Calle 38 # 54A-35',
    description: 'Dirección legible. Si no hay coordenadas y la geocodificación está activa, se usa para ubicar el lugar.',
    aliases: ['direccion', 'direccioncompleta', 'address', 'streetaddress', 'dir', 'domicilio'] },
  { key: 'postalCode', label: 'Código postal', group: 'Ubicación administrativa', column: 'codigo_postal', maxLength: 12, example: '054040',
    description: 'Opcional.',
    aliases: ['codigopostal', 'postalcode', 'zipcode', 'zip', 'cp'] },
  { key: 'lat', label: 'Latitud', group: 'Ubicación geográfica', column: 'latitud', maxLength: 40, example: '6.15385',
    description: 'Grados decimales, WGS84. En Colombia va de −4,3 a 13,5.',
    aliases: ['latitud', 'latitude', 'lat', 'y'] },
  { key: 'lng', label: 'Longitud', group: 'Ubicación geográfica', column: 'longitud', maxLength: 40, example: '-75.37412',
    description: 'Grados decimales, WGS84. En Colombia es negativa: de −82 a −66,8.',
    aliases: ['longitud', 'longitude', 'lng', 'lon', 'long', 'x'] },
  { key: 'coordinates', label: 'Coordenadas (latitud, longitud)', group: 'Ubicación geográfica', column: 'coordenadas', maxLength: 80, example: '6.15385, -75.37412',
    description: 'Alternativa a las dos columnas: latitud y longitud en una sola celda.',
    aliases: ['coordenadas', 'coordinates', 'latlng', 'latlon', 'latitudlongitud', 'ubicacion', 'geo'] },
  { key: 'crs', label: 'Sistema de referencia', group: 'Ubicación geográfica', column: 'sistema_referencia', maxLength: 40, example: 'WGS84',
    description: 'WGS84 (EPSG:4326) o MAGNA-SIRGAS geográficas (EPSG:4686). Las coordenadas planas no se aceptan.',
    aliases: ['sistemareferencia', 'sistemadereferencia', 'crs', 'srid', 'epsg', 'datum'] },
  { key: 'radiusM', label: 'Radio (m)', group: 'Ubicación geográfica', column: 'radio_m', maxLength: 10, example: '60',
    description: 'Metros alrededor del punto que cuentan como «estar ahí» (5 a 5000). Vacío: el habitual para el tipo.',
    aliases: ['radiom', 'radio', 'radius', 'radiometros'] },
  { key: 'instructions', label: 'Indicaciones para el conductor', group: 'Información adicional', column: 'indicaciones', maxLength: 300, example: 'Recoger en la portería norte',
    description: 'Hasta 300 caracteres.',
    aliases: ['indicaciones', 'instrucciones', 'instructions', 'comollegar'] },
  { key: 'phone', label: 'Teléfono', group: 'Información adicional', column: 'telefono', maxLength: 40, example: '+57 604 5699999',
    description: 'Teléfono del lugar (no de una persona).',
    aliases: ['telefono', 'tel', 'phone', 'telephone', 'celular', 'movil'] },
  { key: 'website', label: 'Sitio web', group: 'Información adicional', column: 'sitio_web', maxLength: 200, example: 'https://www.clinicasomer.com',
    description: 'Dirección http o https.',
    aliases: ['sitioweb', 'paginaweb', 'website', 'web', 'url'] },
  { key: 'hours', label: 'Horario', group: 'Información adicional', column: 'horario', maxLength: 200, example: 'Lun-Vie 7:00-18:00',
    description: 'Texto libre.',
    aliases: ['horario', 'horarios', 'horariodeatencion', 'hours', 'openinghours'] },
  { key: 'accessibility', label: 'Accesibilidad', group: 'Información adicional', column: 'accesibilidad', maxLength: 200, example: 'Rampa en la entrada principal',
    description: 'Solo información verificada.',
    aliases: ['accesibilidad', 'accessibility', 'wheelchair'] },
  { key: 'status', label: 'Estado', group: 'Información adicional', column: 'estado', maxLength: 20, example: 'Activo',
    description: 'Activo o Inactivo. Vacío: activo.',
    aliases: ['estado', 'status', 'activo'] },
];

export const IMPORT_FIELD_KEYS = IMPORT_FIELDS.map((f) => f.key);
const FIELD = new Map(IMPORT_FIELDS.map((f) => [f.key, f]));
export const importField = (key: ImportFieldKey): ImportFieldDef => FIELD.get(key)!;

/** field -> zero-based column of the file. Columns nobody points at are ignored. */
export type ImportMapping = Partial<Record<ImportFieldKey, number>>;

/**
 * Column of the file for each field, by its header. A header is given to one field only; fields are
 * visited in catalog order and their aliases from most to least specific, so "municipio" wins the
 * municipality over "localidad", which then falls to the locality.
 */
export function suggestMapping(headers: readonly string[]): ImportMapping {
  const folded = headers.map((h) => foldKey(h ?? ''));
  const taken = new Set<number>();
  const out: ImportMapping = {};
  for (const f of IMPORT_FIELDS) {
    for (const alias of [foldKey(f.column), ...f.aliases]) {
      const i = folded.findIndex((h, idx) => h === alias && !taken.has(idx));
      if (i >= 0) {
        out[f.key] = i;
        taken.add(i);
        break;
      }
    }
  }
  return out;
}

export interface MappingProblem {
  field: ImportFieldKey;
  message: string;
}

/** What a mapping lacks to be importable at all (row problems are found later, row by row). */
export function mappingProblems(mapping: ImportMapping, columns: number, opts: { geocode: boolean; hasDefaultMunicipality: boolean }): MappingProblem[] {
  const out: MappingProblem[] = [];
  const has = (k: ImportFieldKey) => mapping[k] !== undefined;
  const used = new Map<number, ImportFieldKey>();
  for (const [k, col] of Object.entries(mapping) as Array<[ImportFieldKey, number]>) {
    if (!Number.isInteger(col) || col < 0 || col >= columns) out.push({ field: k, message: `La columna elegida para «${importField(k).label}» no existe en el archivo.` });
    else if (used.has(col)) out.push({ field: k, message: `La misma columna está asignada a «${importField(used.get(col)!).label}» y a «${importField(k).label}».` });
    else used.set(col, k);
  }
  if (!has('name')) out.push({ field: 'name', message: 'Falta la columna con el nombre del lugar.' });
  const point = (has('lat') && has('lng')) || has('coordinates');
  if (has('lat') !== has('lng')) out.push({ field: has('lat') ? 'lng' : 'lat', message: 'Latitud y longitud van juntas: falta una de las dos columnas.' });
  if (!point && !(opts.geocode && has('address')))
    out.push({
      field: 'lat',
      message: opts.geocode
        ? 'El archivo no trae coordenadas ni dirección: no hay cómo ubicar los lugares.'
        : 'El archivo no trae coordenadas. Asigna latitud y longitud, o activa la geocodificación si trae direcciones.',
    });
  if (!point && !has('municipality') && !has('municipalityCode') && !opts.hasDefaultMunicipality)
    out.push({ field: 'municipality', message: 'Sin coordenadas hace falta el municipio: asigna la columna o elige un municipio por defecto.' });
  return out;
}

// ---------------------------------------------------------------------------- values
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const CONTROL = new RegExp('[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2060-\u206f\ufeff]', 'g');

/** Whitespace and invisible characters only: the words, case and accents the person wrote are kept. */
export function cleanText(raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  return String(raw).normalize('NFC').replace(CONTROL, '').replace(/\s+/g, ' ').trim();
}

/** "6.1734", "6,1734", "6° 10' 24.2\" N", "75°22'26\"W" -> decimal degrees, or null. */
export function parseCoordinate(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const dms = /^(-?\d{1,3})\s*[°º]\s*(\d{1,2})\s*['′’]\s*(\d{1,2}(?:[.,]\d+)?)\s*(?:["″”]|'')?\s*([NSEWO])?$/i.exec(s);
  if (dms) {
    const deg = Number(dms[1]);
    const v = Math.abs(deg) + Number(dms[2]) / 60 + Number(dms[3]!.replace(',', '.')) / 3600;
    const west = /[SWO]/i.test(dms[4] ?? '') || deg < 0;
    return Number((west ? -v : v).toFixed(7));
  }
  // one decimal separator, comma or point; thousands separators make no sense in a coordinate
  if (!/^[-+]?\d{1,3}(?:[.,]\d+)?$/.test(s)) return null;
  const v = Number(s.replace(',', '.'));
  return Number.isFinite(v) ? v : null;
}

/** "6.15385, -75.37412" / "6,15385; -75,37412" / "6.15385 -75.37412" -> the two parts, or null. */
export function splitCoordinatePair(raw: string): [string, string] | null {
  const s = raw.trim().replace(/^[([]|[)\]]$/g, '');
  if (!s) return null;
  const candidates: Array<[string, string]> = [];
  const add = (parts: string[]) => {
    if (parts.length === 2 && parts[0] && parts[1]) candidates.push([parts[0], parts[1]]);
  };
  for (const sep of [';', '|', '\t']) add(s.split(sep).map((x) => x.trim()));
  // "6.15385, -75.37412" and "6,15385 -75,37412": the space separates, a comma may be the decimal mark
  add(s.split(/\s+/).map((x) => x.replace(/,$/, '')));
  const byComma = s.split(',').map((x) => x.trim());
  add(byComma);
  if (byComma.length === 4) add([`${byComma[0]},${byComma[1]}`, `${byComma[2]},${byComma[3]}`]);
  return candidates.find(([a, b]) => parseCoordinate(a) !== null && parseCoordinate(b) !== null) ?? candidates[0] ?? null;
}

export interface CountryBounds {
  code: string;
  name: string;
  /** folded spellings accepted in a "country" column */
  names: string[];
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

/** Extent of each supported country (mainland and islands). A new market adds a row here. */
export const COUNTRY_BOUNDS: Record<string, CountryBounds> = {
  CO: { code: 'CO', name: 'Colombia', names: ['colombia', 'co', 'col', 'republicadecolombia'], minLat: -4.3, maxLat: 13.5, minLng: -82, maxLng: -66.8 },
};

const inBounds = (lat: number, lng: number, b: CountryBounds) => lat >= b.minLat && lat <= b.maxLat && lng >= b.minLng && lng <= b.maxLng;

export type PointProblem = 'partial' | 'invalid' | 'zero' | 'out_of_range' | 'out_of_country';
export type PointResult = { ok: true; lat: number; lng: number; swapped: boolean } | { ok: false; problem: PointProblem } | { ok: null };

/**
 * A usable point from the two cells. Swapped coordinates are corrected only when it is the only
 * reading that makes sense: the pair as written is outside the country and the swapped one is inside.
 */
export function readPoint(latRaw: string, lngRaw: string, bounds: CountryBounds): PointResult {
  const a = latRaw.trim();
  const b = lngRaw.trim();
  if (!a && !b) return { ok: null };
  if (!a || !b) return { ok: false, problem: 'partial' };
  const lat = parseCoordinate(a);
  const lng = parseCoordinate(b);
  // plain large numbers: planar coordinates in metres (MAGNA-SIRGAS Colombia Bogotá and the like)
  const planar = (s: string) => /^[-+]?\d{4,}(?:[.,]\d+)?$/.test(s);
  if (lat === null || lng === null) return { ok: false, problem: planar(a) || planar(b) ? 'out_of_range' : 'invalid' };
  if (lat === 0 && lng === 0) return { ok: false, problem: 'zero' };
  if (inBounds(lat, lng, bounds)) return { ok: true, lat, lng, swapped: false };
  if (inBounds(lng, lat, bounds)) return { ok: true, lat: lng, lng: lat, swapped: true };
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return { ok: false, problem: 'out_of_range' };
  return { ok: false, problem: 'out_of_country' };
}

/** WGS84 and MAGNA-SIRGAS geographic are the same for our purposes (centimetres apart). */
export function supportedCrs(raw: string): boolean {
  const k = foldKey(raw).replace(/^epsg/, '');
  return ['', '4326', '4686', 'wgs84', 'wgs1984', 'magna', 'magnasirgas', 'sirgas', 'gps', 'crs84'].includes(k);
}

/** "5615" (Excel drops the leading zero), "05615", "05.615" -> "05615"; anything else -> null. */
export function divipolaCode(raw: string): string | null {
  const d = raw.replace(/[\s.-]/g, '');
  if (!/^\d{4,5}$/.test(d)) return null;
  return d.padStart(5, '0');
}

export function parseTags(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of raw.split(/[;|,]/).map((x) => cleanText(x).slice(0, 40)).filter(Boolean)) {
    const k = foldText(t);
    if (k && !seen.has(k)) {
      seen.add(k);
      out.push(t);
    }
  }
  return out.slice(0, 10);
}

/** Digits with an optional leading "+", 7 to 15 of them; otherwise null. */
export function normalizePhone(raw: string): string | null {
  const s = raw.replace(/[^\d+]/g, '');
  const digits = s.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return null;
  return (s.startsWith('+') ? '+' : '') + digits;
}

/** An http(s) URL (the scheme is added when missing); anything else -> null. Never `javascript:` and friends. */
export function normalizeWebsite(raw: string): string | null {
  const s = raw.trim();
  if (!s || /\s/.test(s)) return null;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(s)?.[1]?.toLowerCase();
  if (scheme && scheme !== 'http' && scheme !== 'https') return null;
  const candidate = scheme ? s : `https://${s}`;
  // scheme, a host with at least one dot, optional port and path
  const m = /^(https?):\/\/([a-z0-9-]+(?:\.[a-z0-9-]+)+)(:\d{1,5})?([/?#][^\s]*)?$/i.exec(candidate);
  if (!m) return null;
  return `${m[1]!.toLowerCase()}://${m[2]!.toLowerCase()}${m[3] ?? ''}${m[4] ?? ''}`.slice(0, 200);
}

export function parseStatus(raw: string): 'active' | 'inactive' | null {
  const k = foldKey(raw);
  if (['', 'activo', 'activa', 'active', 'si', 'yes', 'true', '1', 'vigente', 'habilitado'].includes(k)) return 'active';
  if (['inactivo', 'inactiva', 'inactive', 'no', 'false', '0', 'cerrado', 'deshabilitado', 'baja'].includes(k)) return 'inactive';
  return null;
}

// ---------------------------------------------------------------------------- rows
/** error: cannot be imported · review: a person must decide or complete it · warning: imported, worth a look */
export type IssueLevel = 'error' | 'review' | 'warning';
export interface ImportIssue {
  level: IssueLevel;
  /** the field of the system the problem is about (what the person edits to fix it) */
  field: ImportFieldKey | null;
  code: string;
  message: string;
  /** what to do about it */
  hint?: string;
}

/** A row after normalization: what would be stored, before anything is looked up in the database. */
export interface ImportRowData {
  name: string;
  altNames: string[];
  description: string | null;
  externalId: string | null;
  kind: TenantPlaceKind | null;
  category: PlaceCategory | null;
  /** the category as the file wrote it, when it is not in the catalog */
  sourceCategory: string | null;
  subcategory: string | null;
  tags: string[];
  department: string | null;
  municipality: string | null;
  municipalityCode: string | null;
  locality: string | null;
  address: string | null;
  postalCode: string | null;
  lat: number | null;
  lng: number | null;
  radiusM: number | null;
  instructions: string | null;
  phone: string | null;
  website: string | null;
  hours: string | null;
  accessibility: string | null;
  status: 'active' | 'inactive';
}

export interface NormalizedRow {
  data: ImportRowData;
  issues: ImportIssue[];
  /** what was changed with respect to the file, for traceability ("coordenadas intercambiadas") */
  transformations: string[];
}

/** The level of a row: its worst issue. */
export function rowLevel(issues: readonly ImportIssue[]): 'ok' | IssueLevel {
  if (issues.some((i) => i.level === 'error')) return 'error';
  if (issues.some((i) => i.level === 'review')) return 'review';
  return issues.some((i) => i.level === 'warning') ? 'warning' : 'ok';
}

const POINT_MESSAGES: Record<PointProblem, { message: string; hint: string }> = {
  partial: { message: 'Falta la latitud o la longitud.', hint: 'Escribe las dos o deja ambas vacías.' },
  invalid: { message: 'Las coordenadas no son números válidos.', hint: 'Usa grados decimales, por ejemplo 6.15385 y -75.37412.' },
  zero: { message: 'Las coordenadas son 0, 0.', hint: 'Suele ser un valor por defecto del sistema de origen: escribe el punto real.' },
  out_of_range: { message: 'Las coordenadas están fuera del rango posible (latitud ±90, longitud ±180).', hint: 'Si son coordenadas planas (metros), conviértelas a WGS84.' },
  out_of_country: { message: 'El punto queda fuera de Colombia.', hint: 'Revisa el signo: en Colombia la longitud es negativa.' },
};

/**
 * One row of the file -> normalized data + what is wrong with it. `raw` holds the cells already
 * picked by the mapping (text as read). Pure: municipality, duplicates and geocoding come later.
 */
export function normalizeImportRow(raw: Partial<Record<ImportFieldKey, string>>, opts: { country?: CountryBounds; defaultKind?: TenantPlaceKind } = {}): NormalizedRow {
  const country = opts.country ?? COUNTRY_BOUNDS.CO!;
  const issues: ImportIssue[] = [];
  const transformations: string[] = [];
  const cell = (k: ImportFieldKey): string => {
    // the apostrophe our own exports put before a cell a spreadsheet would read as a formula
    const v = cleanText(raw[k] ?? '').replace(/^'(?=[=+@-])/, '');
    const max = importField(k).maxLength;
    if (v.length > max) {
      issues.push({ level: 'warning', field: k, code: 'TRUNCATED', message: `«${importField(k).label}» tenía ${v.length} caracteres y se recortó a ${max}.` });
      return v.slice(0, max).trim();
    }
    return v;
  };
  const text = (k: ImportFieldKey): string | null => cell(k) || null;

  // -- identification
  const name = cell('name');
  if (!name) issues.push({ level: 'error', field: 'name', code: 'NAME_REQUIRED', message: 'Falta el nombre del lugar.', hint: 'Escribe el nombre o excluye la fila.' });
  else if (name.length < 2 || !/\p{L}/u.test(name)) issues.push({ level: 'error', field: 'name', code: 'NAME_INVALID', message: 'El nombre debe tener al menos dos caracteres y alguna letra.' });
  const key = name ? nameKey(name) : '';
  const altNames: string[] = [];
  for (const a of cell('altName').split(/[;|]/).map((x) => cleanText(x).slice(0, 160))) {
    if (a.length >= 2 && /\p{L}/u.test(a) && nameKey(a) !== key && !altNames.some((x) => nameKey(x) === nameKey(a))) altNames.push(a);
  }

  // -- classification
  let kind: TenantPlaceKind | null = opts.defaultKind ?? null;
  const kindRaw = cell('kind');
  if (kindRaw) {
    const m = matchKind(kindRaw);
    if (m && 'kind' in m) kind = m.kind;
    else if (m)
      issues.push({
        level: 'error', field: 'kind', code: 'KIND_OFFICIAL',
        message: `«${kindRaw}» es una división oficial: barrios, veredas y municipios los mantiene la plataforma.`,
        hint: 'Importa aquí sitios, puntos de recogida, conjuntos, urbanizaciones o sectores.',
      });
    else
      issues.push({
        level: 'warning', field: 'kind', code: 'KIND_UNKNOWN',
        message: `No se reconoce el tipo «${kindRaw}»: se usará el tipo por defecto.`,
        hint: 'Tipos válidos: Punto de recogida, Sitio, Referencia, Conjunto, Urbanización, Sector.',
      });
  }
  let category: PlaceCategory | null = null;
  let sourceCategory: string | null = null;
  const catRaw = cell('category');
  if (catRaw) {
    category = matchCategory(catRaw);
    if (!category) {
      category = 'other';
      sourceCategory = catRaw;
      issues.push({ level: 'warning', field: 'category', code: 'CATEGORY_UNKNOWN', message: `La categoría «${catRaw}» no está en el catálogo: quedará como «Otro».`, hint: 'Elige una categoría del catálogo si alguna le corresponde.' });
    } else if (foldKey(catRaw) !== category) transformations.push(`Categoría «${catRaw}» → ${category}`);
  }

  // -- administrative location
  const countryRaw = cell('country');
  if (countryRaw && !country.names.includes(foldKey(countryRaw)))
    issues.push({ level: 'error', field: 'country', code: 'COUNTRY_UNSUPPORTED', message: `El país «${countryRaw}» no está habilitado: solo se importan lugares de ${country.name}.` });
  let municipalityCode: string | null = null;
  const codeRaw = cell('municipalityCode');
  if (codeRaw) {
    municipalityCode = divipolaCode(codeRaw);
    if (!municipalityCode)
      issues.push({ level: 'error', field: 'municipalityCode', code: 'DIVIPOLA_INVALID', message: `«${codeRaw}» no es un código DANE de municipio.`, hint: 'Son cinco dígitos: dos del departamento y tres del municipio (Rionegro: 05615).' });
    else if (municipalityCode !== codeRaw) transformations.push(`Código DANE «${codeRaw}» → ${municipalityCode}`);
  }

  // -- point
  let lat: number | null = null;
  let lng: number | null = null;
  let latRaw = cell('lat');
  let lngRaw = cell('lng');
  const pair = cell('coordinates');
  if (!latRaw && !lngRaw && pair) {
    const p = splitCoordinatePair(pair);
    if (p) [latRaw, lngRaw] = p;
    else issues.push({ level: 'error', field: 'coordinates', code: 'POINT_INVALID', message: `No se entiende «${pair}» como latitud y longitud.`, hint: 'Escribe las dos separadas por coma: 6.15385, -75.37412.' });
  }
  const crs = cell('crs');
  if (crs && !supportedCrs(crs))
    issues.push({ level: 'error', field: 'crs', code: 'CRS_UNSUPPORTED', message: `El sistema de referencia «${crs}» no se admite.`, hint: 'Convierte las coordenadas a WGS84 (EPSG:4326) en grados decimales.' });
  else {
    const point = readPoint(latRaw, lngRaw, country);
    if (point.ok) {
      lat = point.lat;
      lng = point.lng;
      if (point.swapped) {
        transformations.push('Latitud y longitud estaban intercambiadas');
        issues.push({ level: 'warning', field: 'lat', code: 'POINT_SWAPPED', message: 'La latitud y la longitud venían intercambiadas: se corrigieron.', hint: 'Comprueba el punto en el mapa.' });
      }
    } else if (point.ok === false) {
      const m = POINT_MESSAGES[point.problem];
      issues.push({ level: 'error', field: 'lat', code: `POINT_${point.problem.toUpperCase()}`, message: m.message, hint: m.hint });
    }
  }
  let radiusM: number | null = null;
  const radiusRaw = cell('radiusM');
  if (radiusRaw) {
    const r = Number(radiusRaw.replace(',', '.'));
    if (Number.isFinite(r) && r >= 5 && r <= 5000) radiusM = Math.round(r);
    else issues.push({ level: 'warning', field: 'radiusM', code: 'RADIUS_INVALID', message: `El radio «${radiusRaw}» no está entre 5 y 5000 metros: se usará el habitual para el tipo.` });
  }

  // -- additional information
  let phone: string | null = null;
  const phoneRaw = cell('phone');
  if (phoneRaw) {
    phone = normalizePhone(phoneRaw);
    if (!phone) issues.push({ level: 'warning', field: 'phone', code: 'PHONE_INVALID', message: `«${phoneRaw}» no parece un teléfono: no se guardará.` });
  }
  let website: string | null = null;
  const webRaw = cell('website');
  if (webRaw) {
    website = normalizeWebsite(webRaw);
    if (!website) issues.push({ level: 'warning', field: 'website', code: 'WEBSITE_INVALID', message: `«${webRaw}» no es una dirección web válida: no se guardará.`, hint: 'Debe empezar por http:// o https://.' });
  }
  let status: 'active' | 'inactive' = 'active';
  const statusRaw = cell('status');
  if (statusRaw) {
    const s = parseStatus(statusRaw);
    if (s) status = s;
    else issues.push({ level: 'warning', field: 'status', code: 'STATUS_UNKNOWN', message: `No se reconoce el estado «${statusRaw}»: quedará activo.` });
  }

  return {
    data: {
      name,
      altNames: altNames.slice(0, 8),
      description: text('description'),
      externalId: text('externalId'),
      kind,
      category,
      sourceCategory,
      subcategory: text('subcategory'),
      tags: parseTags(cell('tags')),
      department: text('department'),
      municipality: text('municipality'),
      municipalityCode,
      locality: text('locality'),
      address: text('address'),
      postalCode: text('postalCode'),
      lat,
      lng,
      radiusM,
      instructions: text('instructions'),
      phone,
      website,
      hours: text('hours'),
      accessibility: text('accessibility'),
      status,
    },
    issues,
    transformations,
  };
}

/** "alcaldia-2026" from "Alcaldía 2026": the prefix of the external identifiers of one data source. */
export function sourceSlug(label: string): string {
  return foldText(label).replace(/\s+/g, '-').slice(0, 40) || 'archivo';
}
